<template>
  <div id="app">
    <router-view v-if="tryOnce" />
    <custom-loader v-if="overlayMixinOverlay || overlayMixinOverlayGrid" />
    <f-toast ref="toast" />
    <confirm-dialog v-if="tryOnce" ref="confirm-dialog" />
    <alert-dialog v-if="tryOnce" ref="alert-dialog" />
    <pos-confirm-dialog
      ref="pos-confirm-dialog"
      v-if="visiblePosConfirmDialog"
      @close="onPosConfirmDialogClose"
    />
  </div>
</template>

<script>
import Vue from "vue";
import VueViewer from 'v-viewer';
import { mapActions, mapGetters, mapMutations } from "vuex";
import { ToastPlugin } from "@syncfusion/ej2-vue-notifications";
import { isOverrideApplicationShortcut } from "@/utils/KeyboardUtil";
import overlayMixin from "@/views/layout/mixin/overlayMixin";
import CustomLoader from "@/components/common/CustomLoader";
import ConfirmDialog from "@/components/dialog/ConfirmDialog";
import AlertDialog from "@/components/dialog/AlertDialog";
import FToast from "@/components/toast/FToast";
import PosConfirmDialog from "@/views/store-management/sales-registration/store-pos-renew/dialogs/ConfirmDialog";
import 'viewerjs/dist/viewer.css';
import GolfERPService from "@/service/GolfERPService";
import {getFcmMessage} from "@/utils/pushNofication";
import {setBizCodesObject} from "@/utils/pageKeyUtil";
import {orderBy as _orderBy} from "lodash";

Vue.use(ToastPlugin);
Vue.use(VueViewer, {
  defaultOptions: {
    title: false,
    navbar: false,
  }
});

export default {
  name: "App",
  mixins: [overlayMixin],
  components: { FToast, CustomLoader, AlertDialog, ConfirmDialog, PosConfirmDialog },
  created() {
    this.$EventBus.$on("logout", ({ isSessionExpired } = {}) =>
        GolfERPService.logout({
          isSessionExpired,
        })
    );
    this.$EventBus.$on("errToast", (message, listeners) => this.showErrorToast(message, listeners));
    this.$EventBus.$on("infoToast", (message, listeners) => this.showInfoToast(message, listeners));
    this.$EventBus.$on("pinnedToast", ({ message, listeners }) =>
      this.showPinnedToast({ message, listeners })
    );
    this.$EventBus.$on("sessionExpiredToast", ({ listeners } = {}) =>
      this.showSessionExpiredToast({ listeners })
    );
    this.$EventBus.$on(
      "taskToast",
      ({ message, promise, interrupt, listeners }) =>
        this.showTaskToast({ message, promise, interrupt, listeners })
    );

    // global keyboard key down event handler
    window.addEventListener(
      "keydown",
      (e) => {
        if (
          isOverrideApplicationShortcut(e.ctrlKey, e.altKey, e.shiftKey, e.key)
        ) {
          e.preventDefault();
        }
      },
      true
    );

    this.$EventBus.$on("loaderOn", () => this.overlayMixinOverlay = true);

    this.$EventBus.$on("loaderOff", () => this.overlayMixinOverlay = false);

    this.$EventBus.$on("gridLoaderOn", () => this.overlayMixinOverlayGrid = true);

    this.$EventBus.$on("gridLoaderOff", () => this.overlayMixinOverlayGrid = false);

    this.$EventBus.$on("confirm", (payload) => {
      this.$refs["confirm-dialog"].show({
        title: payload.title,
        message: payload.message,
        callback: payload.callback,
      });
    });

    this.$EventBus.$on("alert", (payload) => {
      this.$refs["alert-dialog"].show({
        title: payload.title || this.$t("main.title.errorPopup"),
        message: payload.message,
        closeCallback: payload.closeCallback,
      });
    });
    this.$EventBus.$on("pos-confirm", (payload) => {
      if (this.visiblePosConfirmDialog) {
        return;
      }

      this.setVisibleConfirmDialog({ visibleConfirmDialog: true });
      this.$nextTick(() => {
        this.$refs["pos-confirm-dialog"].show({
          message: payload.message,
          ...(payload.confirmBtnLabel &&  { confirmBtnLabel : payload.confirmBtnLabel}),
          ...(payload.cancelBtnLabel && { cancelBtnLabel: payload.cancelBtnLabel}),
          callback: payload.callback,
        });
      });
    });
  },
  async mounted() {
    const businessPlaces = await GolfERPService.fetchBusinessPlaces();
    setBizCodesObject(
        _orderBy(businessPlaces, "order").map(({ dbms, code, name }) => ({
          dbms,
          bizCode: code,
          bizName: name,
        }))
    );

    // Dialog에서도 동작하게 하기 위하여 document에 click event 추가
    document.addEventListener(
      "click",
      () => {
        this.overlayMixinCheckIsApiOverlayOpened();
        this.overlayMixinCheckIsGridOveralyOpened();
      },
      true
    );

    const firebaseAppMessage = getFcmMessage();
    if (firebaseAppMessage) {
      this.$store.dispatch("fcm/setFcmToken", firebaseAppMessage)
          .then((token) => {
            console.log("get FCM Token", token);
          })
          .catch((e) => {
            console.error(e);
          });
    }

    try {
      await this.loadApplication();
    } finally {
      this.tryOnce = true;
    }
  },
  beforeDestroy() {
    this.$EventBus.$off("logout");
    this.$EventBus.$off("errToast");
    this.$EventBus.$off("infoToast");
    this.$EventBus.$off("pinnedToast");
    this.$EventBus.$off("sessionExpiredToast");
    this.$EventBus.$off("taskToast");
    this.$EventBus.$off("loaderOn");
    this.$EventBus.$off("loaderOff");
    this.$EventBus.$off("gridLoaderOn");
    this.$EventBus.$off("gridLoaderOff");
    this.$EventBus.$off("confirm");
    this.$EventBus.$off("alert");
    this.$EventBus.$off("pos-confirm");
    document.removeEventListener(
      "click",
      () => {
        this.overlayMixinCheckIsApiOverlayOpened();
        this.overlayMixinCheckIsGridOveralyOpened();
      },
      true
    );
    window.removeEventListener("keydown", (e) => {
          if (
              isOverrideApplicationShortcut(e.ctrlKey, e.altKey, e.shiftKey, e.key)
          ) {
            e.preventDefault();
          }
        },
        true);

  },
  data() {
    return {
      tryOnce: false,
      isOverlayShow: false,
      overlayMixinOverlay: false,
      overlayMixinOverlayGrid: false,
      toastPosition: { X: "Right", Y: "Bottom" },
    };
  },
  computed: {
    ...mapGetters("pos", {
      visiblePosConfirmDialog: "visibleConfirmDialog",
    }),
  },
  methods: {
    ...mapActions(["loadApplication"]),
    ...mapMutations("pos", ["setVisibleConfirmDialog"]),
    showErrorToast(message, listeners) {
      if (message) {
        this.$refs.toast.error({
          message,
          listeners
        });
      }
    },
    showInfoToast(message, listeners) {
      if (message) {
        this.$refs.toast.info(
          message,
          listeners
        );
      }
    },
    showPinnedToast({ message, listeners }) {
      if (message) {
        this.$refs.toast.pinned({
          message,
          listeners,
        });
      }
    },
    showSessionExpiredToast({ listeners } = {}) {
      this.$refs.toast.sessionExpired({
        listeners,
      });
    },
    showTaskToast({ message, promise, interrupt, listeners }) {
      this.$refs.toast.task({
        message,
        promise,
        interrupt,
        listeners,
      });
    },

    onPosConfirmDialogClose() {
      this.setVisibleConfirmDialog({ visibleConfirmDialog: false });
    },
  },
};
</script>

<style>
/* Vue 관련 css로 변경. */
@import "../node_modules/@syncfusion/ej2-base/styles/material.css";
@import "../node_modules/@syncfusion/ej2-vue-buttons/styles/material.css";
@import "../node_modules/@syncfusion/ej2-vue-calendars/styles/material.css";
@import "../node_modules/@syncfusion/ej2-vue-dropdowns/styles/material.css";
@import "../node_modules/@syncfusion/ej2-vue-grids/styles/material.css";
@import "../node_modules/@syncfusion/ej2-vue-inputs/styles/material.css";
@import "../node_modules/@syncfusion/ej2-vue-navigations/styles/material.css";
@import "../node_modules/@syncfusion/ej2-vue-popups/styles/material.css";
@import "../node_modules/@syncfusion/ej2-vue-splitbuttons/styles/material.css";
@import "../node_modules/@syncfusion/ej2-vue-schedule/styles/material.css";
@import "../node_modules/@syncfusion/ej2-vue-notifications/styles/material.css";

@font-face {font-family: "Toast_icons";src: url(data:application/x-font-ttf;charset=utf-8;base64,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) format("truetype");font-weight: normal;font-style: normal;}

#toast_types button {margin: 5px;min-width: 160px;max-width: 160px;}
.toast-icons {font-family: "Toast_icons" !important;speak: none;font-size: 55px;font-style: normal;font-weight: normal;font-variant: normal;text-transform: none;line-height: 1;-webkit-font-smoothing: antialiased;-moz-osx-font-smoothing: grayscale;}
#toast_type .e-toast-icon.e-icons {height: auto;font-size: 30px;}
.toast-icons.e-info::before {content: "\e704";}
.toast-icons.e-error::before {content: "\e700";}
</style>
